import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { readJoinIntent } from './memberJoin'
import { GANESHA_LOGO_POSTER, GANESHA_LOGO_VIDEO, LIFF_ID_BOT1, LIFF_ID_BOT2 } from '../config/env'
import { SchemeToggle } from '../components/layout/SchemeToggle'
import { resolveBotIdFromHref } from '../lib/orderUtils'
import { isLineInAppBrowser } from '../hooks/useLiff'
import { memberEntryUrl } from '../lib/lineNavigation'
import { useEnergySaving } from '../lib/energyMode'
import { MemberLandingIcon as Icon, type LandingIconName } from './MemberLandingIcon'
import { AdminContact } from './AdminContact'

const GUIDE_VIDEO = '/ordercus-guide.mp4'
const GUIDE_POSTER = '/ordercus-guide-poster.jpg'
/** วิดีโอวนลูปควันกำยาน/เปลวเทียนหน้าองค์เทพ (~0.6MB) · poster = เฟรมแรกของลูป */
const TEMPLE_LOOP_MP4 = '/temple-loop.mp4'
const TEMPLE_LOOP_WEBM = '/temple-loop.webm'
const TEMPLE_LOOP_POSTER = '/temple-loop-poster.jpg'

/** หน้าแรกออกแบบใหม่ 13 ต.ค. 2569: 1 ข้อความ · 1 ปุ่มหลัก — ส่วนล่างเป็นรายการ/ลำดับ ไม่ใช่การ์ดซ้ำ */
const FEATURES: ReadonlyArray<{ icon: LandingIconName; title: string; desc: string }> = [
  { icon: 'order', title: 'ตรวจสอบและชำระออร์เดอร์', desc: 'ดูสถานะล่าสุด แล้วชำระหรือยืนยันข้อมูลต่อบนเว็บได้เลย' },
  { icon: 'user', title: 'ใช้ข้อมูลเดิมได้เลย', desc: 'บันทึกชื่อ เบอร์โทร ที่อยู่ และวันเกิดไว้ ไม่ต้องกรอกซ้ำ' },
  { icon: 'truck', title: 'ติดตามการจัดส่ง', desc: 'ดูสถานะและเลขพัสดุเมื่อทีมงานจัดส่งแล้ว' },
  { icon: 'gift', title: 'กิจกรรมสมาชิก', desc: 'รับข่าวสารและร่วมกิจกรรมของเทวาลัย' },
]
const STEPS: ReadonlyArray<{ title: string; desc: string }> = [
  { title: 'เข้าสู่ระบบด้วย LINE', desc: 'ใช้บัญชีเดียวกับที่สั่งบูชา ระบบจะแสดงออร์เดอร์ของคุณให้เอง' },
  { title: 'ตรวจสอบข้อมูลของคุณ', desc: 'ตรวจชื่อ เบอร์โทร และที่อยู่ให้ถูกต้อง แล้วบันทึกไว้ใช้ครั้งต่อไป' },
  { title: 'เลือกออร์เดอร์ แล้วทำต่อได้เลย', desc: 'ชำระบูชา กรอกข้อมูล และติดตามการจัดส่ง ทั้งหมดบนเว็บ' },
]


function useLandingMotion() {
  const [motion, setMotion] = useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  const [visible, setVisible] = useState(() => !document.hidden)
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onMotion = () => setMotion(!media.matches)
    const onVisibility = () => setVisible(!document.hidden)
    media.addEventListener('change', onMotion)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      media.removeEventListener('change', onMotion)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
  return { motion, visible }
}

/** เลื่อนถึงแล้วค่อยปรากฏ — ไม่มี motion/IO = เนื้อหาเห็นครบเหมือนเดิม (ไม่ซ่อนอะไรเลย) */
function useScrollReveal(rootRef: { current: HTMLElement | null }, motion: boolean) {
  useEffect(() => {
    const root = rootRef.current
    if (!root || !motion || typeof IntersectionObserver === 'undefined') return
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        entry.target.classList.add('is-in')
        observer.unobserve(entry.target)
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    root.setAttribute('data-reveal-ready', '')
    nodes.forEach((node) => observer.observe(node))
    return () => {
      observer.disconnect()
      root.removeAttribute('data-reveal-ready')
      nodes.forEach((node) => node.classList.remove('is-in'))
    }
  }, [rootRef, motion])
}

/** เลื่อนจอ: แถบความคืบหน้า + ตัวแปร --sy (เฉพาะฉากแรก) ให้ CSS ทำ parallax · ตั้งผ่าน rAF ครั้งละหนึ่งเฟรม */
function useScrollMotion(sceneRef: { current: HTMLElement | null }, barRef: { current: HTMLElement | null }, motion: boolean) {
  useEffect(() => {
    const scene = sceneRef.current
    const bar = barRef.current
    if (!scene || !motion) return
    let frame = 0
    let lastY = -1
    const update = () => {
      frame = 0
      const y = Math.max(0, Math.round(window.scrollY || 0))
      if (y === lastY) return
      lastY = y
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
      if (bar) bar.style.transform = `scaleX(${Math.min(1, y / max).toFixed(4)})`
      scene.style.setProperty('--sy', String(Math.min(y, 1400)))
    }
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      scene.style.removeProperty('--sy')
      if (bar) bar.style.transform = ''
    }
  }, [sceneRef, barRef, motion])
}

function LandingLogo({ motion: motionProp, paused }: { motion: boolean; paused: boolean }) {
  // โหมดประหยัดพลังงาน: ภาพนิ่งแทนวิดีโอ (ไม่แตะ motion ของฉากเลื่อน — ถ้าปิดฉากนั้นเนื้อหาจะไม่ปรากฏ)
  const energySaving = useEnergySaving()
  const motion = motionProp && !energySaving
  const [failed, setFailed] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (paused) video.pause()
    else void video.play().catch(() => {})
  }, [motion, paused, failed])
  if (!motion || failed) return <img className="mlp-logo mo-landing-logo" src={GANESHA_LOGO_POSTER} alt="ตราศรีคเนศ เทวาลัย" width="58" height="58" />
  return <video ref={videoRef} className="mlp-logo mo-landing-logo" src={GANESHA_LOGO_VIDEO} poster={GANESHA_LOGO_POSTER} width="58" height="58" autoPlay muted loop playsInline preload="metadata" aria-label="ตราศรีคเนศ เทวาลัย" onError={() => setFailed(true)} />
}

function LineMark() { return <span className="mlp-line-mark" aria-hidden="true">LINE</span> }

/** เน็ตประหยัด/2G = ใช้ภาพนิ่งแทนวิดีโอ (Safari ไม่มี navigator.connection → ถือว่าปกติ) */
function prefersLiteMedia(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
  return !!connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')
}

/** ฉากหลังเวที: วิดีโอวนลูป โหลดเมื่อใกล้เข้าจอ · หยุดเมื่อพ้นจอ/สลับแท็บ/เปิด dialog · ไม่มี motion = ภาพนิ่ง */
function TempleVideo({ motion: motionProp, paused }: { motion: boolean; paused: boolean }) {
  const energySaving = useEnergySaving()
  const motion = motionProp && !energySaving
  const artRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [near, setNear] = useState(false)
  const [inView, setInView] = useState(false)
  const [failed, setFailed] = useState(false)
  const [lite] = useState(prefersLiteMedia)
  const enabled = motion && !lite && !failed
  useEffect(() => {
    const node = artRef.current
    if (!node || !enabled) return
    if (typeof IntersectionObserver === 'undefined') { setNear(true); setInView(true); return }
    const observer = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting)
      if (entry.isIntersecting) setNear(true)
    }, { rootMargin: '240px 0px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled])
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (paused || !inView) video.pause()
    else void video.play().catch(() => {})
  }, [paused, inView, near, enabled])
  return <div ref={artRef} className="lp-media" aria-hidden="true">
    {enabled && near
      ? <video ref={videoRef} className="lp-media-el" poster={TEMPLE_LOOP_POSTER} autoPlay muted loop playsInline preload="auto"><source src={TEMPLE_LOOP_WEBM} type="video/webm" /><source src={TEMPLE_LOOP_MP4} type="video/mp4" onError={() => setFailed(true)} /></video>
      : <img className="lp-media-el" src={TEMPLE_LOOP_POSTER} alt="" decoding="async" />}
  </div>
}

export default function MemberLandingPage() {
  const [guideOpen, setGuideOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const menuRef = useRef<HTMLElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<HTMLElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const { motion, visible } = useLandingMotion()
  useScrollReveal(rootRef, motion)
  useScrollMotion(sceneRef, progressRef, motion)
  const botId = resolveBotIdFromHref() === '2' ? '2' : '1'
  // มาจากการ์ดชวนสมัคร — หัวข้อเป็นการสมัคร และเข้าสู่ระบบแล้วไปฟอร์มสมัครต่อ
  const joining = readJoinIntent(window.location.search)
  const ordersUrl = memberEntryUrl(botId === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1, isLineInAppBrowser(), botId, '', true, joining)
  const paused = !motion || !visible || guideOpen

  useEffect(() => {
    if (!guideOpen || !dialogRef.current) return
    const node = dialogRef.current
    const menuButton = menuButtonRef.current
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    node.showModal()
    return () => {
      node.querySelector('video')?.pause()
      node.close()
      document.body.style.overflow = previousOverflow
      menuButton?.focus()
    }
  }, [guideOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButtonRef.current?.focus() }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('pointerdown', onPointerDown); document.removeEventListener('keydown', onKeyDown) }
  }, [menuOpen])

  const openGuide = (_event: MouseEvent<HTMLButtonElement>) => {
    setMenuOpen(false)
    setGuideOpen(true)
  }

  // ปุ่มเข้าสู่ระบบบนแถบบนซ่อนตอนเห็นปุ่ม LINE ใหญ่ในฉากแรก (ไม่ให้ปุ่มซ้ำกัน) · เลื่อนพ้น/ปุ่มอยู่ใต้จอ = โผล่ขึ้นมา
  useEffect(() => {
    const root = rootRef.current
    const cta = root?.querySelector('.lp-hero .lp-cta')
    if (!root || !cta || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => {
      root.dataset.heroCta = entry.isIntersecting ? 'in' : 'out'
    }, { rootMargin: '-64px 0px 0px 0px', threshold: 0.6 })
    observer.observe(cta)
    return () => { observer.disconnect(); delete root.dataset.heroCta }
  }, [])

  return <div ref={rootRef} className="mo-landing lp" data-motion={paused ? 'paused' : 'active'}>
    <div className="lp-backdrop" aria-hidden="true"><img src={TEMPLE_LOOP_POSTER} alt="" decoding="async" /></div>
    <a className="mlp-skip-link" href="#mlp-main">ข้ามไปเนื้อหา</a>
    <div ref={progressRef} className="mlp-progress" aria-hidden="true" />
    <header ref={menuRef} className="mlp-nav-shell">
      <nav className="mlp-nav-pill" aria-label="เมนูหลัก">
        <button ref={menuButtonRef} type="button" className="mlp-menu-toggle" aria-label={menuOpen ? 'ปิดเมนู' : 'เปิดเมนู'} aria-controls="mlp-mobile-menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}><Icon name={menuOpen ? 'close' : 'menu'} /></button>
        <a className="mlp-nav-login mlp-line-login-green mlp-shine mo-landing-login" href={ordersUrl} aria-label="เข้าสู่ระบบสมาชิกด้วย LINE">เข้าสู่ระบบ</a>
        <SchemeToggle />
      </nav>
      <div id="mlp-mobile-menu" className="mlp-menu-panel" hidden={!menuOpen}>
        <a href="#mlp-services" onClick={() => setMenuOpen(false)}>บริการสมาชิก</a><a href="#mlp-how" onClick={() => setMenuOpen(false)}>เริ่มใช้งานอย่างไร</a><a href={ordersUrl} onClick={() => setMenuOpen(false)}>เข้าสู่หน้าสมาชิก</a><button type="button" onClick={openGuide}>วิธีชำระบูชา</button><AdminContact onOpen={() => setMenuOpen(false)} />
      </div>
    </header>

    <main id="mlp-main">
      <section ref={sceneRef} className="lp-hero" id="mlp-home" aria-labelledby="mo-landing-title">
        <TempleVideo motion={motion} paused={paused} />
        <div className="lp-veil" aria-hidden="true" />
        <div className="lp-hero-content">
          <LandingLogo motion={motion} paused={paused} />
          <p className="lp-brand">ศรีคเนศ เทวาลัย · ระบบสมาชิก</p>
          {joining ? <>
            <h1 id="mo-landing-title">สมัครครอบครัว<br />ศรีคเนศ</h1>
            <p className="lp-lede"><span>รับสิทธิประโยชน์สมาชิก สะสมแต้มทุกยอดบูชา</span><span>เข้าสู่ระบบด้วย LINE แล้วกรอกข้อมูลให้ครบ ใช้เวลาไม่ถึง 1 นาที</span></p>
          </> : <>
            <h1 id="mo-landing-title">ออร์เดอร์บูชาของคุณ<br />อยู่ในที่เดียว</h1>
            <p className="lp-lede"><span>ชำระบูชา ติดตามการจัดส่ง และใช้ข้อมูลเดิมได้ทันที</span><span>เข้าด้วย LINE เดิม ไม่ต้องสมัครใหม่</span></p>
          </>}
          <a className="lp-cta mlp-line-login-green mlp-shine mo-landing-login" href={ordersUrl}><LineMark />เข้าสู่ระบบด้วย LINE</a>
          <p className="lp-ask lp-ask--hero"><span>มีคำถาม?</span><AdminContact /></p>
        </div>
      </section>

      <section id="mlp-services" className="lp-section" aria-labelledby="mlp-services-title">
        <h2 id="mlp-services-title" data-reveal>ในหน้าสมาชิก คุณทำได้ 4 อย่าง</h2>
        <ul className="lp-list" data-reveal>
          {FEATURES.map((item) => <li key={item.title}><span className="lp-icon"><Icon name={item.icon} /></span><div><h3>{item.title}</h3><p>{item.desc}</p></div></li>)}
        </ul>
      </section>

      <section id="mlp-how" className="lp-section" aria-labelledby="mlp-how-title">
        <h2 id="mlp-how-title" data-reveal>เริ่มต้นใน 3 ขั้นตอน</h2>
        <ol className="lp-steps" data-reveal>
          {STEPS.map((step, index) => <li key={step.title}><span className="lp-step-no" aria-hidden="true">{index + 1}</span><div><h3>{step.title}</h3><p>{step.desc}</p></div></li>)}
        </ol>
      </section>

      <section className="lp-section" aria-labelledby="lp-closing-title">
        <div className="lp-closing" data-reveal>
          <h2 id="lp-closing-title">พร้อมดูออร์เดอร์ของคุณแล้วหรือยัง</h2>
          <p>ใช้ LINE เดิมของคุณได้เลย</p>
          <a className="lp-cta mlp-line-login-green mlp-shine mo-landing-login" href={ordersUrl}><LineMark />เข้าสู่ระบบด้วย LINE</a>
          <p className="lp-ask"><span>มีคำถาม?</span><AdminContact /></p>
        </div>
      </section>
    </main>

    <footer className="lp-footer">ศรีคเนศ เทวาลัย · ระบบสมาชิก</footer>

    <dialog ref={dialogRef} className="mlp-glass-dialog mlp-glass-dialog--guide" aria-labelledby="mo-landing-dialog-title" onCancel={() => setGuideOpen(false)} onClick={(event) => {
      if (event.target !== event.currentTarget) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) setGuideOpen(false)
    }}>
      <button autoFocus type="button" className="mlp-dialog-close" onClick={() => setGuideOpen(false)} aria-label="ปิดหน้าต่าง"><Icon name="close" /></button>
      {guideOpen && <><h2 id="mo-landing-dialog-title">วิธีชำระบูชา</h2><video className="mlp-dialog-video" controls playsInline preload="none" poster={GUIDE_POSTER} aria-label="วิดีโอแนะนำการใช้งาน OrderCus"><source src={GUIDE_VIDEO} type="video/mp4" />เบราว์เซอร์นี้ไม่รองรับการเล่นวิดีโอ</video></>}
    </dialog>
  </div>
}
